// ── Parser da planilha de vidas (infra) ───────────────────────────
// Lê o arquivo que o comercial envia e devolve linhas já mapeadas por
// coluna. NÃO valida nada: quem decide se um CPF presta é o núcleo
// (`validarPlanilha` em @modules/cs). Aqui só se resolve formato.
//
// Dois formatos:
//   • CSV  — parser próprio, sem dependência. É o caminho garantido.
//   • XLSX — leitor próprio (xlsxGrid) por import() dinâmico, para o bundle
//            principal não carregar ZIP + XML que 90% das sessões não usam.
//
// O modelo entregue ao cliente é o xlsx (public/modelos/modelo-de-vidas-
// bluehub.xlsx e o -completo-) e tem DUAS tabelas de pessoas:
//   Colaborador — o titular, com o plano contratado e as marcações de
//                 adicional (odonto, psicólogo na tela);
//   Vida+       — o dependente, que aponta o colaborador vinculado por nome,
//                 CPF e telefone.
// As abas "Plano Odontológico Clin", "Psicólogo na Tela" e "Resumo" são
// derivadas dessas duas dentro do próprio Excel — ler qualquer uma delas
// criaria duas verdades sobre o mesmo número.
//
// O cabeçalho é procurado, não assumido: planilha que passou por
// "salvar como" perde linhas de topo com frequência.

import { normalizeText } from '@modules/cs';
import type { ImportacaoFormato, NegociacaoPlanilha, PlanilhaLinha, VidaTipo } from '@modules/cs';

export interface PlanilhaLida {
  formato: ImportacaoFormato;
  linhas: PlanilhaLinha[];
  /** sha256 do arquivo original, gravado junto com a importação. */
  hash: string;
  /** Cabeçalhos que não foram reconhecidos — a UI avisa em vez de
   *  descartar em silêncio. */
  colunasIgnoradas: string[];
  negociacao: NegociacaoPlanilha | null;
}

export class PlanilhaFormatoError extends Error {}
export class PlanilhaSemCabecalhoError extends Error {}

// ── Mapeamento de coluna ──────────────────────────────────────────
// Por palavra-chave e não por título exato: a planilha volta do cliente
// com "E-mail", "Email", "e-mail do colaborador"...
//
// A ORDEM IMPORTA. "Nome do colaborador vinculado" também casa com /^nome/,
// e "Plano Odontológico Clin?" também casa com /plano/. As colunas mais
// específicas vêm primeiro para que a genérica só pegue o que sobrou.
const COLUNAS: Array<{ campo: keyof PlanilhaLinha; match: RegExp }> = [
  { campo: 'titularNome',     match: /^nome.*(vinculad|titular)/ },
  { campo: 'titularCpf',      match: /cpf.*(vinculad|titular)/ },
  { campo: 'titularTelefone', match: /(telefone|celular).*(vinculad|titular)/ },
  // Antes de /odonto/ e de /^nome/: o título é "Nome da Mãe (se Odonto = Sim)".
  // Só "Nome da mãe"/"Mãe": "E-mail da mãe" ou "Telefone da mãe" não podem cair aqui.
  { campo: 'nomeMae',         match: /^nome.*\bmae\b|^mae\b/ },
  { campo: 'odonto',          match: /odonto/ },
  { campo: 'psicologo',       match: /psicolog|zenklub/ },
  { campo: 'nome',            match: /^nome/ },
  { campo: 'cpf',             match: /cpf/ },
  { campo: 'dataNascimento',  match: /nascimento|nasc\.?$/ },
  { campo: 'email',           match: /mail/ },
  { campo: 'telefone',        match: /telefone|celular|fone|whats/ },
  { campo: 'plano',           match: /plano/ },
];

/** Colunas auxiliares do próprio modelo (numeram as relações automáticas).
 *  Não são dado e não merecem o aviso de "coluna não reconhecida". */
const COLUNAS_DESCARTAVEIS = /^seq\.?\s/;

/** Quais campos precisam existir para a linha ser um cabeçalho de tabela. */
const CAMPOS_ESSENCIAIS: Array<keyof PlanilhaLinha> = [
  'nome', 'cpf', 'dataNascimento', 'email', 'telefone', 'plano',
];

// Sem acento e sem caixa. É a MESMA função do núcleo, e não uma cópia: o
// título da coluna que o parser casa e o nome do plano que o validador casa
// têm que normalizar igual, senão "Odontológico" bate num lado e não no
// outro. (A classe de acentos vive lá, escrita com escapes — combinante
// solto no código some em copy/paste.)
const norm = normalizeText;

const campoDaColuna = (titulo: string): keyof PlanilhaLinha | null => {
  const achado = COLUNAS.find(col => col.match.test(titulo));
  return achado ? achado.campo : null;
};

/** Quantas colunas ESSENCIAIS esta linha reconhece. As do titular não
 *  contam: uma linha de instrução que cite "colaborador vinculado" não pode
 *  virar cabeçalho por causa disso. */
const pontuarCabecalho = (celulas: unknown[]): number => {
  const vistos = new Set<string>();
  celulas.forEach((c) => {
    const t = norm(c);
    if (!t) return;
    const campo = campoDaColuna(t);
    if (campo && (CAMPOS_ESSENCIAIS as string[]).includes(campo)) vistos.add(campo);
  });
  return vistos.size;
};

/**
 * Acha a linha de cabeçalho e devolve o índice de cada campo.
 * Exige 4 das 6 colunas essenciais: com menos que isso, o que veio não é a
 * tabela (as linhas 1–11 do modelo são texto de instrução e casariam "plano"
 * sozinho pelo título "PLANOS BLUEHUB").
 */
const acharCabecalho = (
  matriz: unknown[][],
): { indice: number; mapa: Partial<Record<keyof PlanilhaLinha, number>>; ignoradas: string[] } => {
  const LIMITE_BUSCA = 40;
  let melhor = { linha: -1, pontos: 0 };

  matriz.slice(0, LIMITE_BUSCA).forEach((celulas, i) => {
    const pontos = pontuarCabecalho(celulas || []);
    if (pontos > melhor.pontos) melhor = { linha: i, pontos };
  });

  if (melhor.pontos < 4) {
    throw new PlanilhaSemCabecalhoError(
      'Não encontrei a linha de cabeçalho. Use o modelo e mantenha as colunas '
      + 'Nome, CPF, Data de Nascimento, E-mail, Telefone e Plano Contratado.',
    );
  }

  const mapa: Partial<Record<keyof PlanilhaLinha, number>> = {};
  const ignoradas: string[] = [];

  (matriz[melhor.linha] || []).forEach((celula, coluna) => {
    const t = norm(celula);
    if (!t) return;
    const campo = campoDaColuna(t);
    // Primeira ocorrência vence: duas colunas que caiam no mesmo campo não
    // podem deixar a segunda sobrescrever a primeira.
    if (campo && mapa[campo] === undefined) mapa[campo] = coluna;
    else if (!COLUNAS_DESCARTAVEIS.test(t)) ignoradas.push(String(celula).trim());
  });

  return { indice: melhor.linha, mapa, ignoradas };
};

const celula = (linha: unknown[], indice: number | undefined): string =>
  indice === undefined ? '' : String(linha?.[indice] ?? '').trim();

/**
 * Dependente ou titular? A aba já diz, mas o CSV não tem abas — e uma
 * planilha só de dependentes chega assim com frequência. Um vínculo
 * preenchido ou o plano Vida+ na linha resolvem sozinhos.
 *
 * Devolve só `'colaborador' | 'vida_mais'` — nunca `'so_adicional'`. Decisão
 * de propósito: o parser só sabe dizer "é dependente", e não sabe se o
 * dependente tem plano ou não — quem lê a célula de Plano e decide Vida+ x
 * só adicional (`ehSemPlano`) é o núcleo, em `validarPlanilha`. Manter essa
 * decisão fora da infra evita duplicar a regra "Nenhum Plano contratado"
 * em dois lugares que podem divergir.
 */
const tipoDaLinha = (
  padrao: VidaTipo,
  titularNome: string,
  titularCpf: string,
  plano: string,
): VidaTipo => {
  if (titularNome || titularCpf) return 'vida_mais';
  if (/vida\s*\+|vida\s*mais/.test(norm(plano))) return 'vida_mais';
  return padrao;
};

const matrizParaLinhas = (
  matriz: unknown[][],
  tipoPadrao: VidaTipo,
  aba?: string,
): { linhas: PlanilhaLinha[]; ignoradas: string[] } => {
  const { indice, mapa, ignoradas } = acharCabecalho(matriz);

  const linhas = matriz.slice(indice + 1).map((celulas, i) => {
    const titularNome = celula(celulas, mapa.titularNome);
    const titularCpf = celula(celulas, mapa.titularCpf);
    const plano = celula(celulas, mapa.plano);

    return {
      // +1 porque planilha é 1-indexada, +1 pelo próprio cabeçalho.
      // Assim o erro cita o mesmo número que o comercial vê no Excel.
      numero: indice + i + 2,
      aba,
      tipo: tipoDaLinha(tipoPadrao, titularNome, titularCpf, plano),
      nome:           celula(celulas, mapa.nome),
      cpf:            celula(celulas, mapa.cpf),
      dataNascimento: celula(celulas, mapa.dataNascimento),
      email:          celula(celulas, mapa.email),
      telefone:       celula(celulas, mapa.telefone),
      plano,
      titularNome,
      titularCpf,
      titularTelefone: celula(celulas, mapa.titularTelefone),
      odonto:    celula(celulas, mapa.odonto),
      psicologo: celula(celulas, mapa.psicologo),
      nomeMae:   celula(celulas, mapa.nomeMae),
    };
  });

  // Linha sem nada em nenhuma coluna conhecida não é dado. O modelo aplica
  // formatação e validação até a linha 212 de cada aba, e o Excel guarda
  // essas linhas vazias no arquivo — sem este corte, uma planilha com 2
  // pessoas chegaria ao núcleo com quase mil linhas para descartar.
  return { linhas: linhas.filter(l => !vazia(l)), ignoradas };
};

const vazia = (l: PlanilhaLinha): boolean =>
  ![l.nome, l.cpf, l.dataNascimento, l.email, l.telefone, l.plano,
    l.titularNome, l.titularCpf, l.odonto, l.psicologo]
    .some(v => String(v ?? '').trim());

// ── CSV ───────────────────────────────────────────────────────────

/**
 * Delimitador por contagem na primeira linha não-vazia.
 * O Excel brasileiro exporta com `;` (a vírgula é o separador decimal),
 * mas o modelo baixado de outras ferramentas vem com `,`. Adivinhar
 * errado transforma a planilha inteira numa coluna só.
 */
const detectarDelimitador = (texto: string): string => {
  const primeira = texto.split(/\r?\n/).find(l => l.trim()) || '';
  const candidatos = [';', ',', '\t'];
  return candidatos
    .map(d => ({ d, n: primeira.split(d).length }))
    .sort((a, b) => b.n - a.n)[0].d;
};

/** Parser CSV com aspas (RFC 4180): campo entre aspas pode conter o
 *  delimitador e quebra de linha, e `""` é uma aspa literal. */
const parseCsv = (texto: string, delimitador: string): string[][] => {
  const matriz: string[][] = [];
  let linha: string[] = [];
  let campo = '';
  let dentroDeAspas = false;

  for (let i = 0; i < texto.length; i += 1) {
    const c = texto[i];

    if (dentroDeAspas) {
      if (c === '"') {
        if (texto[i + 1] === '"') { campo += '"'; i += 1; }
        else dentroDeAspas = false;
      } else campo += c;
      continue;
    }

    if (c === '"') { dentroDeAspas = true; continue; }
    if (c === delimitador) { linha.push(campo); campo = ''; continue; }
    if (c === '\r') continue;
    if (c === '\n') { linha.push(campo); matriz.push(linha); linha = []; campo = ''; continue; }
    campo += c;
  }

  if (campo || linha.length) { linha.push(campo); matriz.push(linha); }
  return matriz;
};

const lerCsv = async (file: Blob): Promise<unknown[][]> => {
  let texto = await file.text();
  // BOM do Excel. Sem remover, o primeiro cabeçalho vira "﻿Nome"
  // e nunca casa com /^nome/.
  if (texto.charCodeAt(0) === 0xfeff) texto = texto.slice(1);
  return parseCsv(texto, detectarDelimitador(texto));
};

// ── XLSX ──────────────────────────────────────────────────────────

/** As abas que têm gente. A ordem é a da leitura, e é ela que garante o
 *  titular antes do dependente na lista devolvida. */
export const ABAS_DE_PESSOAS: Array<{ match: RegExp; tipo: VidaTipo }> = [
  { match: /colaborador/,            tipo: 'colaborador' },
  { match: /vida\s*\+|vida\s*mais|dependentes?/, tipo: 'vida_mais' },
];

interface AbaLida { aba: string; tipo: VidaTipo; matriz: unknown[][] }
interface XlsxLida { abas: AbaLida[]; negociacao: NegociacaoPlanilha | null }

const extrairNegociacao = (matriz: unknown[][] | undefined): NegociacaoPlanilha | null => {
  if (!matriz) return null;
  const resumo = matriz
    .map(linha => ({ item: String(linha?.[0] ?? '').trim(), valores: (linha || []).slice(1).map(v => String(v ?? '').trim()).filter(Boolean) }))
    .filter(linha => linha.item && linha.valores.length);
  return resumo.length ? { resumo } : null;
};

/**
 * O leitor de .xlsx entra por import() dinâmico: o código de ZIP + XML só
 * baixa quando alguém escolhe um .xlsx, e a sessão que manda CSV não paga
 * por ele.
 */
const lerXlsx = async (file: Blob): Promise<XlsxLida> => {
  const { lerXlsxGrades, XlsxLeituraError } = await import('./xlsxGrid');
  try {
    const grades = lerXlsxGrades(await file.arrayBuffer());
    const comPessoas: AbaLida[] = [];
    const negociacao = extrairNegociacao(grades.find(g => /resumo/.test(g.aba.toLowerCase()))?.linhas);

    ABAS_DE_PESSOAS.forEach(({ match, tipo }) => {
      grades
        .filter(g => match.test(g.aba.toLowerCase()))
        .forEach(g => comPessoas.push({ aba: g.aba, tipo, matriz: g.linhas }));
    });

    // Nenhuma aba com nome conhecido: a planilha foi renomeada ou é de
    // outra origem. A primeira aba vira a de colaboradores — que é o
    // comportamento que sempre existiu.
    if (!comPessoas.length && grades.length) {
      return { abas: [{ aba: grades[0].aba, tipo: 'colaborador', matriz: grades[0].linhas }], negociacao };
    }
    return { abas: comPessoas, negociacao };
  } catch (e) {
    // Erro de leitura vira mensagem de formato: para quem enviou a
    // planilha, a distinção entre "ZIP corrompido" e "não é xlsx" não
    // muda o que ele precisa fazer.
    if (e instanceof XlsxLeituraError) throw new PlanilhaFormatoError(e.message);
    throw e;
  }
};

// ── Hash ──────────────────────────────────────────────────────────

const sha256 = async (file: Blob): Promise<string> => {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  // Contexto não-seguro (http) não expõe crypto.subtle. O hash é para
  // rastreabilidade, não para segurança — vale seguir sem ele.
  if (!subtle) return '';
  const digest = await subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// ── Entrada pública ───────────────────────────────────────────────

const formatoDoNome = (nome: string): ImportacaoFormato => {
  const ext = nome.toLowerCase().split('.').pop() || '';
  if (ext === 'csv' || ext === 'txt') return 'csv';
  if (ext === 'xlsx' || ext === 'xlsm' || ext === 'xls') return 'xlsx';
  throw new PlanilhaFormatoError(`Formato .${ext} não suportado. Envie .csv ou .xlsx.`);
};

export const lerPlanilhaVidas = async (
  file: File,
): Promise<PlanilhaLida> => {
  const formato = formatoDoNome(file.name || '');

  const lida = formato === 'csv'
    ? { abas: [{ aba: '', tipo: 'colaborador' as VidaTipo, matriz: await lerCsv(file) }], negociacao: null }
    : await lerXlsx(file);
  const abas = lida.abas;

  if (!abas.length || !abas.some(a => a.matriz.length)) {
    throw new PlanilhaSemCabecalhoError('A planilha está vazia.');
  }

  const linhas: PlanilhaLinha[] = [];
  const ignoradas: string[] = [];
  let ultimoErro: unknown = null;

  abas.forEach(({ aba, tipo, matriz }) => {
    try {
      const lidas = matrizParaLinhas(matriz, tipo, aba || undefined);
      linhas.push(...lidas.linhas);
      ignoradas.push(...lidas.ignoradas);
    } catch (e) {
      // Uma aba sem tabela não derruba o arquivo: é o caso de a empresa não
      // ter nenhum Vida+ e ter apagado a tabela da aba. Só falha se NENHUMA
      // aba tiver cabeçalho.
      ultimoErro = e;
    }
  });

  if (!linhas.length && ultimoErro) throw ultimoErro;

  return {
    formato,
    linhas,
    hash: await sha256(file),
    colunasIgnoradas: Array.from(new Set(ignoradas)),
    negociacao: lida.negociacao,
  };
};
